import { AxiosError } from 'axios';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { GET_URL, GET_URL_NEW } from '@utils/api/endpoints';
import { BaseError } from '@utils/types/shared/base';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CustomerBo } from '@utils/types/back-office/customers';
import { QueueSummary } from '@utils/types/back-office/priority-queue';

// Dashboard data types
export interface DashboardCustomerMetrics {
  totalActive: number;
  totalOrganizations: number;
  totalResellers: number;
}

export interface DashboardQueueMetrics {
  queueDepth: number;
  processingCount: number;
  errorCount: number;
  strandedCount: number;
  completedCount: number;
}

export interface DashboardData {
  customers: DashboardCustomerMetrics;
  queue: DashboardQueueMetrics;
  isPartialData: boolean;
}

/**
 * Dashboard hook that aggregates data from multiple existing APIs.
 *
 * Combines:
 * - Customer counts from GET /api/v3/admin/customer/
 * - Queue metrics from GET /api/v3/admin/priority-queue/summary
 *
 * Auto-refreshes every 30 seconds for operational awareness.
 */
export const useGetDashboard = () => {
  const { t } = useTranslation();

  const fetchDashboardData = async (): Promise<DashboardData> => {
    let customers: DashboardCustomerMetrics = {
      totalActive: 0,
      totalOrganizations: 0,
      totalResellers: 0
    };

    let queue: DashboardQueueMetrics = {
      queueDepth: 0,
      processingCount: 0,
      errorCount: 0,
      strandedCount: 0,
      completedCount: 0
    };

    let hasErrors = false;

    // Fetch customers data
    try {
      const { customersBo } = GET_URL_NEW({
        offset: 0,
        limit: 1000 // Get all customers for accurate counts
      });
      const { data: customersData } = await Axios.get<{
        result: CustomerBo[];
        total_results: number;
      }>(customersBo);

      if (customersData?.result) {
        const customerList = customersData.result;
        customers = {
          totalActive: customerList.filter(c => c.is_active).length,
          totalOrganizations:
            customersData.total_results || customerList.length,
          totalResellers: customerList.filter(c => c.is_reseller).length
        };
      }
    } catch {
      hasErrors = true;
    }

    // Fetch queue summary
    try {
      const { priorityQueueSummary } = GET_URL();
      const { data: queueData } =
        await Axios.get<QueueSummary>(priorityQueueSummary);

      if (queueData?.status_counts) {
        const statusMap = new Map<string, number>();
        for (const item of queueData.status_counts) {
          statusMap.set(item.status, item.count);
        }

        queue = {
          queueDepth: statusMap.get('queued') || 0,
          processingCount: statusMap.get('processing') || 0,
          errorCount:
            (statusMap.get('failed') || 0) +
            (statusMap.get('dead_letter') || 0),
          strandedCount: queueData.stuck_processing_count || 0,
          completedCount: statusMap.get('completed') || 0
        };
      }
    } catch {
      hasErrors = true;
    }

    return {
      customers,
      queue,
      isPartialData: hasErrors
    };
  };

  return useQuery<DashboardData, AxiosError<BaseError>>(
    ['dashboard'],
    fetchDashboardData,
    {
      staleTime: 15_000, // Consider fresh for 15 seconds
      cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
      refetchInterval: 30_000, // Auto-refresh every 30 seconds
      refetchOnWindowFocus: true,
      useErrorBoundary: false,
      onError: (error: AxiosError<BaseError>) => {
        const d = error.response?.data;
        const msg =
          d?.error_description ||
          d?.detail ||
          d?.error ||
          d?.message ||
          t('an-error-occurred');
        toast.error(String(msg));
      }
    }
  );
};
